import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

import { cn } from '@/utils/classnames';

const variantClasses = {
  h1: 'text-h1 font-medium text-fx-neutral-900',
  h2: 'text-h2 font-medium text-fx-neutral-900',
  h3: 'text-h3 font-medium text-fx-neutral-900',
  h4: 'text-h4 font-medium text-fx-neutral-900',
  h5: 'text-h5 font-medium text-fx-neutral-900',
  h6: 'text-h6 font-medium text-fx-neutral-900',
  large1: 'text-large font-normal text-fx-neutral-900',
  large2: 'text-large font-medium text-fx-neutral-900',
  medium1: 'text-medium font-normal text-fx-neutral-900',
  medium2: 'text-medium font-medium text-fx-neutral-900',
  body1: 'text-body font-normal text-fx-neutral-900',
  body2: 'text-body font-medium text-fx-neutral-900',
  subtitle1: 'text-body font-normal text-fx-neutral-600',
  subtitle2: 'text-body font-medium text-fx-neutral-600',
  inactive1: 'text-body font-normal text-fx-neutral-400',
  inactive2: 'text-body font-medium text-fx-neutral-400',
  link1: 'text-body font-normal text-fx-brand-600 underline',
  link2: 'text-body font-medium text-fx-brand-600 underline',
  caption:
    'text-caption font-medium text-fx-neutral-600 uppercase tracking-[0.8px]',
  small1: 'text-small font-normal text-fx-neutral-900',
  small2: 'text-small font-medium text-fx-neutral-900',
  text: 'text-text font-normal text-fx-neutral-900',
};

export type TypographyVariant = keyof typeof variantClasses;

type TypographyOwnProps<T extends React.ElementType> = {
  as?: T;
  variant: TypographyVariant;
  className?: string;
  children: React.ReactNode;
  asChild?: boolean;
};

type TypographyProps<T extends React.ElementType> = TypographyOwnProps<T> &
  React.ComponentPropsWithoutRef<T>;

const TypographyBase = React.forwardRef(function TypographyBase(
  {
    as,
    variant,
    className,
    children,
    asChild,
    ...props
  }: TypographyOwnProps<React.ElementType> & React.HTMLAttributes<HTMLElement>,
  ref: React.ForwardedRef<Element>,
) {
  const Component: React.ElementType = asChild ? Slot : (as ?? 'p');

  return (
    <Component
      ref={ref}
      className={cn(variantClasses[variant], className)}
      {...props}
    >
      {children}
    </Component>
  );
});

TypographyBase.displayName = 'Typography';

// forwardRef drops the generic, so the cast restores `as`-driven props.
export const Typography = TypographyBase as <T extends React.ElementType = 'p'>(
  props: TypographyProps<T> & { ref?: React.Ref<Element> },
) => React.ReactElement | null;
